import { IBotData, ISelectOption } from '../../models';
import {
  bot_sa_configurations,
  bot_tunnel_configuration_ids,
  createBotTunnel,
  getAHURules,
} from '../data/bot';

// Air stream IDs
const AIR_STREAM_IDS = {
  SUPPLY: 1,
  RETURN: 2,
  EXHAUST: 4,
} as const;

export const TUNNEL_CONFIG_IDS = {
  SINGLE: 1,
  DUAL_OVER_UNDER: 2,
  DUAL_SIDE_BY_SIDE: 9,
} as const;

// Helper functions
export const valueExistsInOptions = (
  value: string | number | null | undefined,
  options: ISelectOption[] | undefined,
): boolean => {
  if (!options?.length || value === null || value === undefined) {
    return false;
  }
  return options.some((opt) => opt.value === value || opt.value == value);
};

/**
 * Checks if any tunnel has return air (air_stream_id: 2) based on the configuration
 */
export const hasReturnAirTunnel = (data: IBotData): boolean => {
  const airSystemId = data.air_system_id ?? 1;
  const tunnelConfig = data.tunnel_configuration ?? 1;
  const saEaConfig = data.sa_ea_intake_configuration;
  const addReturnAirflow =
    data.tunnels.find((tunnel) => tunnel.add_return_airflow)
      ?.add_return_airflow ?? false;

  // If air_system_id is RETURN_AIR or addReturnAirflow is true
  if (airSystemId === 4 || addReturnAirflow) {
    return true;
  }

  // Check configuration for per_tunnel rules
  const rules = getAHURules(airSystemId, tunnelConfig, saEaConfig);
  if (!rules) return false;

  // Check if any tunnel has return air (air_stream_id: 2)
  if (rules.per_tunnel) {
    return rules.per_tunnel.some(
      (tunnelRule) => tunnelRule.air_stream_id === AIR_STREAM_IDS.RETURN,
    );
  }

  // Check top-level air_stream_id
  return rules.air_stream_id === AIR_STREAM_IDS.RETURN;
};

/**
 * Checks if any tunnel has exhaust air (air_stream_id: 4) based on the configuration
 */
export const hasExhaustAirTunnel = (data: IBotData): boolean => {
  const airSystemId = data.air_system_id ?? 1;
  const tunnelConfig = data.tunnel_configuration ?? 1;
  const saEaConfig = data.sa_ea_intake_configuration;
  // Check configuration for per_tunnel rules
  const rules = getAHURules(airSystemId, tunnelConfig, saEaConfig);
  if (!rules) return false;
  // Check if any tunnel has exhaust air (air_stream_id: 4)
  if (rules.per_tunnel) {
    return rules.per_tunnel.some(
      (tunnelRule) =>
        (tunnelRule.air_stream_id || rules.air_stream_id) ===
        AIR_STREAM_IDS.EXHAUST,
    );
  }
  // Check top-level air_stream_id
  return rules.air_stream_id === AIR_STREAM_IDS.EXHAUST;
};

/**
 * Checks if any tunnel has supply air (air_stream_id: 1) based on the configuration
 */
export const hasSupplyAirTunnel = (data: IBotData): boolean => {
  // Check top-level air_stream_id, default to supply air (1) if not return or exhaust
  return data.air_system_id !== 3 && data.air_system_id !== 4;
};

export const updateAirSystemVolumes = (
  draft: IBotData,
  airSystemId: number,
): void => {
  if (airSystemId !== 5 && draft.tunnel_configuration > 3) {
    draft.tunnel_configuration = 1;
  }

  // Preserve existing flags - only update if they haven't been set yet
  if (draft.hasSupplyAir === undefined) {
    draft.hasSupplyAir = hasSupplyAirTunnel(draft);
  }
  if (draft.hasReturnAir === undefined) {
    draft.hasReturnAir = hasReturnAirTunnel(draft);
  }
  if (draft.hasExhaustAir === undefined) {
    draft.hasExhaustAir = hasExhaustAirTunnel(draft);
  }

  // Use the stored flags (or computed if not stored) for volume updates
  const hasReturnAir = draft.hasReturnAir ?? hasReturnAirTunnel(draft);
  const hasExhaustAir = draft.hasExhaustAir ?? hasExhaustAirTunnel(draft);
  const hasOutsideAirflow = draft.tunnels?.some(
    (tunnel) => tunnel.add_outside_airflow === true,
  );
  draft.hasOutsideAir = hasOutsideAirflow;
  // Update ra_volume_max if return air tunnel exists
  if (hasReturnAir) {
    if (!draft.ra_volume_max) {
      draft.ra_volume_max = 8000;
    }
  }
  // Update ea_volume_max if exhaust air tunnel exists
  if (hasExhaustAir) {
    if (
      draft.sa_volume_max !== undefined &&
      draft.sa_volume_max !== null &&
      !draft.ea_volume_max
    ) {
      draft.ea_volume_max = draft.sa_volume_max;
    }
  }
  // Update oa_volume_max if outside airflow is enabled
  if (hasOutsideAirflow) {
    if (!draft.oa_volume_max) {
      draft.oa_volume_max = 2000;
    }
  } else draft.oa_volume_max = undefined;
};

export const getTunnelIndex = (
  tunnelIndex: number,
  tunnelConfig: number,
): string => {
  if (tunnelIndex === 0) return '1:1';
  if (tunnelIndex === 1) {
    const id =
      bot_tunnel_configuration_ids[
        tunnelConfig as keyof typeof bot_tunnel_configuration_ids
      ];
    if (id === TUNNEL_CONFIG_IDS.DUAL_OVER_UNDER) return '1:2';
    if (id === TUNNEL_CONFIG_IDS.DUAL_SIDE_BY_SIDE) return '2:1';
    return '1:1';
  }
  return '1:1';
};

export const updateTunnelConfiguration = (
  draft: IBotData,
  tunnelConfig: number,
): void => {
  const requiredTunnels = tunnelConfig === TUNNEL_CONFIG_IDS.SINGLE ? 1 : 2;
  const currentTunnelCount = draft.tunnels.length;

  // Preserve existing flags - they will be kept as is
  // (no need to explicitly restore since we're not modifying them)

  // Adjust tunnel count
  if (currentTunnelCount > requiredTunnels) {
    draft.tunnels.splice(requiredTunnels);
  } else if (currentTunnelCount < requiredTunnels) {
    for (let i = currentTunnelCount; i < requiredTunnels; i++) {
      const tunnel_index = getTunnelIndex(i, tunnelConfig);
      draft.tunnels.push(
        createBotTunnel({
          index: `Tunnel ${i + 1}`,
          tunnel_index,
        }),
      );
    }
  }

  // Update tunnel_index for existing tunnels
  draft.tunnels.forEach((tunnel, i) => {
    tunnel.tunnel_index = getTunnelIndex(i, tunnelConfig);
  });

  // When changing to single-tunnel configuration, remove all er_wheel sections
  if (tunnelConfig === TUNNEL_CONFIG_IDS.SINGLE) {
    draft.tunnels.forEach((tunnel) => {
      tunnel.sections = tunnel.sections.filter(
        (section) => section.type !== 'er_wheel',
      );
    });
  }

  // Flags are preserved automatically since we don't modify them here
};

export const validateAndUpdateSAConfiguration = (draft: IBotData): void => {
  const airSystemId = draft.air_system_id ?? 1;
  const tunnelConfig = draft.tunnel_configuration ?? 1;
  const config = (bot_sa_configurations as any)[airSystemId]?.[tunnelConfig];
  const allowedSA = config?.sa_ea_configurations ?? [];

  if (
    allowedSA.length &&
    !allowedSA.some(
      (opt: { value: string }) =>
        opt.value === draft.sa_ea_intake_configuration,
    )
  ) {
    draft.sa_ea_intake_configuration = allowedSA[0].value;
  }
};
